
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  layout: 'reference',
  source: 'endpoint',
  maxTocLevel: 4,
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for institutions endpoints used to fetch institutions-related data. Use these endpoints to assess the health of institutions and more.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const APIVersion = makeShortcode("APIVersion");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Institutions endpoints' subtitle='Fetch data about supported institutions' mdxType="Header" />
    <h4 {...{
      "id": "institution-coverage"
    }}>{`Institution coverage`}</h4>
    <p>{`For a user-friendly overview of which institutions Plaid supports, and the product coverage at each institution, see the `}<a parentName="p" {...{
        "href": "/docs/institutions"
      }}>{`US and Canada Coverage Explorer`}</a>{` or `}<a parentName="p" {...{
        "href": "/docs/institutions/europe"
      }}>{`European Coverage Explorer`}</a>{`. `}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/status"
      }}>{`Institutions page`}</a>{` also provides a browsable view of institutions and supported products, with a focus on reporting institution health and downtimes.`}</p>
    <p>{`For more detailed institution information, or to access this data programmatically, use the API endpoints described on this page. `}</p>
    <h5 {...{
      "id": "supported-countries"
    }}>{`Supported countries`}</h5>
    <p>{`For a list of which products are supported for each country, see `}<a parentName="p" {...{
        "href": "https://support.plaid.com/hc/en-us/articles/27895826947735-What-Plaid-products-are-supported-in-each-country-and-region"
      }}>{`supported products by country`}</a>{` or the docs for the specific product you are interested in.`}</p>
    <p>{`By default, customers in the United States and Canada receive access to institutions in all countries in Sandbox, and to United States and Canada in Production. To gain access to additional countries in Production, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access"
      }}>{`file a product access support ticket`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/institutions/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get a list of all supported institutions meeting specified criteria`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/institutions/get_by_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get details about a specific institution`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/institutions/search`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Look up an institution by name`}</td>
        </tr>
      </tbody>
    </table>
    <Callout mdxType="Callout">
      <p>{`The interface for these endpoints has changed in API version 2020-09-14. If you are using an older API version, see `}<a parentName="p" {...{
          "href": "/docs/api/versioning"
        }}>{`API versioning`}</a>{`.`}</p>
    </Callout>
    <h3 {...{
      "id": "endpoints"
    }}>{`Endpoints`}</h3>
    <h4 {...{
      "id": "institutionsget"
    }}>{`/institutions/get`}</h4>
    <APIVersion version="<2020-09-14" mdxType="APIVersion">
      <Schema method='POST' route='/institutions/get' type='request' mdxType="Schema">
        <MDXCodeBlock type='server' title='/institutions/get' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node"
            }}>{`const request: InstitutionsGetRequest = {
  count: 10,
  offset: 0,
};
try {
  const response = await plaidClient.institutionsGet(request);
  const institutions = response.data.institutions;
} catch (error) {
  // handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/institutions/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "count": Number,
  "offset": Number
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby"
            }}>{`request = Plaid::InstitutionsGetRequest.new(
  {
    count: count,
    offset: offset,
    options: {
      products: ["transactions"],
      include_optional_metadata: true,
    }
  }
)
response = client.institutions_get(request)
institutions = response.institutions
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`InstitutionsGetRequest request = new InstitutionsGetRequest()
  .count(count)
  .offset(offset)
  .countryCodes(Arrays.asList(CountryCode.US));
Response<InstitutionsGetResponse> response = client()
  .institutionsGet(request)
  .execute();
List<Institution> institutions = response.body().getInstitutions();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python"
            }}>{`request = InstitutionsGetRequest(
    count=count,
    offset=offset,
    country_codes=[CountryCode('US')],
)
response = client.institutions_get(request)
institutions = response['institutions']
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go"
            }}>{`request := plaid.NewInstitutionsGetRequest(count, offset, countryCodes)
resp, _, err := client.PlaidApi.InstitutionsGet(ctx).InstitutionsGetRequest(*request).Execute()
if err != nil {
  // handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern",
              "sdk": "fern"
            }}>{`request := &plaid.InstitutionsGetRequest{
    Count:        count,
    Offset:       offset,
    CountryCodes: countryCodes,
}
resp, err := client.Institutions.Get(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
        </MDXCodeBlock>
  
      </Schema>
    </APIVersion>
    <APIVersion version=">=2020-09-14" mdxType="APIVersion">
      <Schema method='POST' route='/institutions/get' type='request' mdxType="Schema">
        <MDXCodeBlock type='server' title='/institutions/get' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node"
            }}>{`// Pull institutions
const request: InstitutionsGetRequest = {
  count: 10,
  offset: 0,
  country_codes: ['US'],
};
try {
  const response = await plaidClient.institutionsGet(request);
  const institutions = response.data.institutions;
} catch (error) {
  // Handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/institutions/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "count": Number,
  "offset": Number,
  "country_codes": Array
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby"
            }}>{`request = Plaid::InstitutionsGetRequest.new(
  {
    count: count,
    offset: offset,
    country_codes: ["US"],
    options: {
      products: ["transactions"],
    }
  }
)
response = client.institutions_get(request)
institutions = response.institutions
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`InstitutionsGetRequest request = new InstitutionsGetRequest()
  .count(count)
  .offset(offset)
  .countryCodes(Arrays.asList(CountryCode.US));
Response<InstitutionsGetResponse> response = client()
  .institutionsGet(request)
  .execute();
List<Institution> institutions = response.body().getInstitutions();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python"
            }}>{`request = InstitutionsGetRequest(
    country_codes=[CountryCode('US')],
    count=count,
    offset=offset
)
response = client.institutions_get(request)
institutions = response['institutions']
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go"
            }}>{`request := plaid.NewInstitutionsGetRequest(count, offset, countryCodes)
resp, _, err := client.PlaidApi.InstitutionsGet(ctx).InstitutionsGetRequest(*request).Execute()
if err != nil {
  // handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern",
              "sdk": "fern"
            }}>{`request := &plaid.InstitutionsGetRequest{
    Count:        count,
    Offset:       offset,
    CountryCodes: countryCodes,
}
resp, err := client.Institutions.Get(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
        </MDXCodeBlock>
  
      </Schema>
    </APIVersion>
    <Schema method='POST' route='/institutions/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "institutionsget_by_id"
    }}>{`/institutions/get_by_id`}</h4>
    <APIVersion version="<2020-09-14" mdxType="APIVersion">
      <Schema method='POST' route='/institutions/get_by_id' type='request' mdxType="Schema">
        <MDXCodeBlock type='server' title='/institutions/get_by_id' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-python"
            }}>{`request = InstitutionsGetByIdRequest(
    institution_id=INSTITUTION_ID,
)
response = client.institutions_get_by_id(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/institutions/get_by_id \\
  -H 'Content-Type: application/json' \\
  -d '{
    "institution_id": "ins_109512",
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>
  }'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby"
            }}>{`request = Plaid::InstitutionsGetByIdRequest.new(
  {
    institution_id: INSTITUTION_ID
  }
)
response = client.institutions_get_by_id(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go"
            }}>{`request := plaid.NewInstitutionsGetByIdRequest(institutionID, countryCodes)
response, _, err := client.PlaidApi.InstitutionsGetById(ctx).InstitutionsGetByIdRequest(*request).Execute()
if err != nil {
  // handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern",
              "sdk": "fern"
            }}>{`request := &plaid.InstitutionsGetByIDRequest{
    InstitutionID: institutionID,
    CountryCodes:  countryCodes,
}
response, err := client.Institutions.GetByID(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`InstitutionsGetByIdRequest request = new InstitutionsGetByIdRequest()
  .institutionId(INSTITUTION_ID);
Response<InstitutionsGetByIdResponse> response = client()
  .institutionsGetById(request)
  .execute();
Institution institution = response.body().getInstitution();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-node"
            }}>{`const request: InstitutionsGetByIdRequest = {
  institution_id: institutionID,
};
try {
  const response = await plaidClient.institutionsGetById(request);
  const institution = response.data.institution;
} catch (error) {
  // Handle error
}
`}</code></pre>
        </MDXCodeBlock>
  
      </Schema>
    </APIVersion>
    <APIVersion version=">=2020-09-14" mdxType="APIVersion">
      <Schema method='POST' route='/institutions/get_by_id' type='request' mdxType="Schema">
        <MDXCodeBlock type='server' title='/institutions/get_by_id' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-python"
            }}>{`request = InstitutionsGetByIdRequest(
    institution_id=INSTITUTION_ID,
    country_codes=[CountryCode('US')]
)
response = client.institutions_get_by_id(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/institutions/get_by_id \\
  -H 'Content-Type: application/json' \\
  -d '{
    "institution_id": "ins_109512",
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "country_codes": Array
  }'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby"
            }}>{`request = Plaid::InstitutionsGetByIdRequest.new(
  {
    institution_id: INSTITUTION_ID,
    country_codes: ["US"]
  }
)
response = client.institutions_get_by_id(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go"
            }}>{`request := plaid.NewInstitutionsGetByIdRequest(institutionID, countryCodes)
response, _, err := client.PlaidApi.InstitutionsGetById(ctx).InstitutionsGetByIdRequest(*request).Execute()
if err != nil {
  // handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern",
              "sdk": "fern"
            }}>{`request := &plaid.InstitutionsGetByIDRequest{
    InstitutionID: institutionID,
    CountryCodes:  countryCodes,
}
response, err := client.Institutions.GetByID(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`InstitutionsGetByIdRequest request = new InstitutionsGetByIdRequest()
  .institutionId(INSTITUTION_ID)
  .addCountryCodesItem(CountryCode.US);
Response<InstitutionsGetByIdResponse> response = client()
  .institutionsGetById(request)
  .execute();
Institution institution = response.body().getInstitution();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-node"
            }}>{`const request: InstitutionsGetByIdRequest = {
  institution_id: institutionID,
  country_codes: ['US'],
};
try {
  const response = await plaidClient.institutionsGetById(request);
  const institution = response.data.institution;
} catch (error) {
  // Handle error
}
`}</code></pre>
        </MDXCodeBlock>
  
      </Schema>
    </APIVersion>
    <Schema method='POST' route='/institutions/get_by_id' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "institutionssearch"
    }}>{`/institutions/search`}</h4>
    <APIVersion version="<2020-09-14" mdxType="APIVersion">
      <Schema method='POST' route='/institutions/search' type='request' mdxType="Schema">
        <MDXCodeBlock type='server' title='/institutions/search' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node"
            }}>{`const request: InstitutionsSearchRequest = {
  query: SEARCH_QUERY,
  products: ['transactions'],
};
try {
  const response = await plaidClient.institutionsSearch(request);
  const institutions = response.data.institutions;
} catch (error) {
  // Handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/institutions/search \\
  -H 'Content-Type: application/json' \\
  -d '{
    "query": "Gingham",
    "products": ["transactions"],
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>
  }'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby"
            }}>{`# search with products
request = Plaid::InstitutionsSearchRequest.new(
  {
    query: SEARCH_QUERY,
    products: ["transactions"]
  }
)
response = client.institutions_search(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`InstitutionsSearchRequest request = new InstitutionsSearchRequest()
  .products(Arrays.asList(Products.IDENTITY))
  .query(SEARCH_QUERY);
Response<InstitutionsSearchResponse> response = client()
  .institutionsSearch(request)
  .execute();
List<Institution> results = response.body().getInstitutions();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python"
            }}>{`request = InstitutionsSearchRequest(
    query=SEARCH_QUERY,
    products=[Products('transactions')]
)
response = client.institutions_search(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go"
            }}>{`request := plaid.NewInstitutionsSearchRequest(query, countryCodes)
request.SetProducts(products)
response, _, err := client.PlaidApi.InstitutionsSearch(ctx).InstitutionsSearchRequest(*request).Execute()
if err != nil {
  // handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern",
              "sdk": "fern"
            }}>{`request := &plaid.InstitutionsSearchRequest{
    Query:        query,
    CountryCodes: countryCodes,
}
request.Products = products
response, err := client.Institutions.Search(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
        </MDXCodeBlock>
      </Schema>
    </APIVersion>
    <APIVersion version=">=2020-09-14" mdxType="APIVersion">
      <Schema method='POST' route='/institutions/search' type='request' mdxType="Schema">
        <MDXCodeBlock type='server' title='/institutions/search' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node"
            }}>{`const request: InstitutionsSearchRequest = {
  query: SEARCH_QUERY,
  products: ['transactions'],
  country_codes: ['US'],
};
try {
  const response = await plaidClient.institutionsSearch(request);
  const institutions = response.data.institutions;
} catch (error) {
  // Handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/institutions/search \\
  -H 'Content-Type: application/json' \\
  -d '{
    "query": "Gingham",
    "products": ["transactions"],
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "country_codes": ["US"]
  }'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby"
            }}>{`# search with products
request = Plaid::InstitutionsSearchRequest.new(
  {
    query: SEARCH_QUERY,
    products: ["transactions"],
    country_codes: ["US"]
  }
)
response = client.institutions_search(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`InstitutionsSearchRequest request = new InstitutionsSearchRequest()
  .countryCodes(Arrays.asList(CountryCode.US))
  .products(Arrays.asList(Products.IDENTITY))
  .query(SEARCH_QUERY);
Response<InstitutionsSearchResponse> response = client()
  .institutionsSearch(request)
  .execute();
List<Institution> results = response.body().getInstitutions();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python"
            }}>{`request = InstitutionsSearchRequest(
    query=SEARCH_QUERY,
    products=[Products('transactions')],
    country_codes=[CountryCode('US')],
)
response = client.institutions_search(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go"
            }}>{`request := plaid.NewInstitutionsSearchRequest(query, countryCodes)
request.SetProducts(products)
response, _, err := client.PlaidApi.InstitutionsSearch(ctx).InstitutionsSearchRequest(*request).Execute()
if err != nil {
  // handle error
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern",
              "sdk": "fern"
            }}>{`request := &plaid.InstitutionsSearchRequest{
    Query:        query,
    CountryCodes: countryCodes,
}
request.Products = products
response, err := client.Institutions.Search(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
        </MDXCodeBlock>
  
      </Schema>
    </APIVersion>
    <Schema method='POST' route='/institutions/search' type='response' mdxType="Schema" />
    <h3 {...{
      "id": "webhooks"
    }}>{`Webhooks`}</h3>
    <p>{`Institution status alerts are configured within the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/team/notification-preferences"
      }}>{`developer dashboard`}</a>{`. In the dashboard, you can choose to receive alerts as either emails or webhooks.`}</p>
    <p>{`All dashboard-configured institution status alerts will have type `}<inlineCode parentName="p">{`DASHBOARD_CONFIGURED_ALERT`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "institution_status_alert_triggered"
    }}><inlineCode parentName="h4">{`INSTITUTION_STATUS_ALERT_TRIGGERED`}</inlineCode></h4>
    <Schema model='InstitutionStatusAlertWebhook' type='model' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "institution-coverage",
        level: 4,
        title: "Institution coverage",
      },
{
        id: "endpoints",
        level: 3,
        title: "Endpoints",
      },
{
        id: "institutionsget",
        level: 4,
        title: "/institutions/get",
      },
{
        id: "institutionsget_by_id",
        level: 4,
        title: "/institutions/get_by_id",
      },
{
        id: "institutionssearch",
        level: 4,
        title: "/institutions/search",
      },
{
        id: "webhooks",
        level: 3,
        title: "Webhooks",
      },
{
        id: "institution_status_alert_triggered",
        level: 4,
        title: "INSTITUTION_STATUS_ALERT_TRIGGERED",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
